import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Newspaper, MapPin, FileText, Plus, Pencil, Trash2, Upload, X, Save,
  Image as ImageIcon, MessageCircle, Instagram, Facebook,
  MessageSquare, Megaphone, Mail, Sparkles,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { MediaUploadCard } from "../components/MediaUploadCard";
import { CampaignsPanel, MarketingSenderPanel } from "../components/newsletter/panels";
import { AgeSelect, ComplianceWarning } from "../components/AgeAndCompliance";
import { useSystemSettings } from "../lib/systemSettings";

/**
 * Admin CMS für das Kundenportal.
 *
 * Tabs: News (mit Bild-Upload, ephemer), Standorte (öffentlicher Finder,
 * unabhängig von internen Lagerorten), Impressum + Social Links.
 * Alle Schreib-Endpunkte sind admin-only auf Backend-Ebene.
 */

const NEWS_EMPTY = { title: "", body: "", image_url: "", priority: 0, active: true, minimum_age: 0 };
const LOC_EMPTY = {
  title: "", type: "automat", street: "", house_no: "", zip: "", city: "",
  description: "", priority: 0, active: true, internal_location_id: "",
  latitude: "", longitude: "", opening_hours: "", image_url: "",
  show_stock: false,
};

export default function AdminKundenportal() {
  return (
    <div className="space-y-6" data-testid="admin-kundenportal">
      <div>
        <div className="text-xs uppercase tracking-widest text-cyan-400">Admin</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Kundenportal
        </h1>
        <div className="text-sm text-zinc-500 mt-1">
          Redaktionelle Inhalte des Kundenportals verwalten.
        </div>
      </div>

      <Tabs defaultValue="news" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
          <TabsTrigger value="news" data-testid="cms-tab-news">
            <Newspaper size={14} className="mr-2" /> News
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="cms-tab-locations">
            <MapPin size={14} className="mr-2" /> Standorte
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="cms-tab-settings">
            <FileText size={14} className="mr-2" /> Impressum & Social
          </TabsTrigger>
          <TabsTrigger value="popup-welcome" data-testid="cms-tab-popup-welcome">
            <MessageSquare size={14} className="mr-2" /> Begrüßungs-Popup
          </TabsTrigger>
          <TabsTrigger value="popup-campaign" data-testid="cms-tab-popup-campaign">
            <Megaphone size={14} className="mr-2" /> Kampagnen-Popup
          </TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="cms-tab-newsletter">
            <Mail size={14} className="mr-2" /> Newsletter
          </TabsTrigger>
          <TabsTrigger value="loop-club" data-testid="cms-tab-loop-club">
            <Sparkles size={14} className="mr-2" /> Loop Club
          </TabsTrigger>
        </TabsList>
        <TabsContent value="news" className="pt-4"><NewsPanel /></TabsContent>
        <TabsContent value="locations" className="pt-4"><LocationsPanel /></TabsContent>
        <TabsContent value="settings" className="pt-4"><SettingsPanel /></TabsContent>
        <TabsContent value="popup-welcome" className="pt-4">
          <PopupEditor kind="welcome" title="Begrüßungs-/Info-Popup" icon={MessageSquare} withCta={false} />
        </TabsContent>
        <TabsContent value="popup-campaign" className="pt-4">
          <PopupEditor kind="campaign" title="News-/Kampagnen-Popup" icon={Megaphone} withCta />
        </TabsContent>
        <TabsContent value="newsletter" className="pt-4">
          <div className="space-y-6" data-testid="cms-newsletter-panel">
            <ComplianceWarning testId="nl-compliance" />
            <MarketingSenderPanel />
            <CampaignsPanel />
          </div>
        </TabsContent>
        <TabsContent value="loop-club" className="pt-4"><LoopClubPanel /></TabsContent>
      </Tabs>
    </div>
  );
}


/**
 * Kundenportal → Loop Club (Admin).
 *
 * Aktuelle interne V1-Kapazität: EIN/AUS-Schalter für Loop Points.
 *
 * Wichtige fachliche Abgrenzung (Owner-Vorgabe 31.08.2026):
 *  - Loop **Club Card / Club Account** = Kundenidentifikation und
 *    langfristig Basis für Kaufhistorie + digitale Belege. Wird bewusst
 *    NICHT als frei deaktivierbares Tenant-Feature dargestellt und hat
 *    hier deshalb KEINEN Admin-Toggle. Der interne technische Kill-Switch
 *    ``club_card_enabled`` in ``system_settings`` bleibt für DEV/QA
 *    erhalten (Default OFF), wird aber nicht in dieser UI angeboten.
 *  - **POS Customer Required** (in Einstellungen → Kasse) bleibt die
 *    einzige Betreiber-Einstellung dafür, ob eine Kundenidentifikation
 *    beim Verkauf zwingend ist.
 *  - **Loop Points** ist das optionale Loyalty-Programm — einzige
 *    Einstellung dieser Ansicht.
 *
 * Ein simples EIN/AUS reicht langfristig nicht (WIND_DOWN benötigt),
 * siehe PRD.md §25 Future Loyalty Lifecycle. Dieses V1-Panel setzt
 * bewusst nur ``points_enabled`` und bleibt vorerst simpel.
 */
function LoopClubPanel() {
  const qc = useQueryClient();
  const { refresh, settings } = useSystemSettings();
  const q = useQuery({
    queryKey: ["admin-system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
  });
  const data = q.data || {};
  // Wahrheitswert: NUR "1" zählt als an — konsistent mit Backend +
  // useFeatureEnabled im Frontend. Kein Fuzzy-Truthy hier.
  const pointsOn = (data.points_enabled ?? settings?.points_enabled ?? "") === "1";

  const save = useMutation({
    mutationFn: async (next) =>
      (await api.put("/admin/system-settings/points_enabled",
        { value: next ? "1" : "" })).data,
    onSuccess: (_r, next) => {
      toast.success(next ? "Loop Punkte aktiviert" : "Loop Punkte deaktiviert");
      qc.invalidateQueries({ queryKey: ["admin-system-settings"] });
      refresh();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen"),
  });

  return (
    <div className="grid gap-6 w-full min-w-0" data-testid="loop-club-panel">
      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-cyan-400 flex items-center gap-2">
          <Sparkles size={14} /> Loop Club — Loyalty
        </div>
        <div className="text-sm text-zinc-400 leading-relaxed">
          Steuert das optionale Loop-Punkte-Programm für die aktuelle
          interne V1. Die Loop Club Karte ist Bestandteil des Kundenkontos
          und immer aktiv — sie wird nicht über diese Ansicht gesteuert.
          Ob ein Verkauf einen zugewiesenen Kunden benötigt, wird
          weiterhin unter <span className="font-mono">Einstellungen → Kasse → Kundenkarte</span> gesteuert.
        </div>

        {/* Loop-Punkte */}
        <div className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-4 flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold uppercase tracking-wider text-zinc-100 mb-1">
              Loop Punkte
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
              1 € Umsatz = 10 Punkte. Bei OFF: keine Neuvergabe, keine
              Punkte-Anzeige im POS-Erfolgs-Screen und im Kundenportal.
              Historische Salden bleiben unverändert und werden bei
              erneutem Aktivieren wieder sichtbar. Ein Einlöse-Flow ist
              in dieser Version noch nicht enthalten.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[11px] uppercase tracking-widest ${pointsOn ? "text-cyan-400" : "text-zinc-500"}`}
                  data-testid="loop-club-points-status">
              {pointsOn ? "AN" : "AUS"}
            </span>
            <Switch
              data-testid="loop-club-points-toggle"
              checked={pointsOn}
              onCheckedChange={(v) => save.mutate(v)}
              disabled={save.isPending}
            />
          </div>
        </div>

        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Änderungen greifen sofort für neue Requests. Bereits geöffnete
          Kundenportal-Sessions aktualisieren die Ansicht beim nächsten
          System-Settings-Refresh (Login/Reload).
        </div>
      </section>
    </div>
  );
}

// ============ Popup Editors (welcome + campaign) ================
// Fachlich Kundenportal-CMS: Redaktion der beim Portal-Login sichtbaren
// Popups. Versionierung + Seen-Tracking bleiben unverändert im Backend.

const POPUP_EMPTY = {
  welcome:  { title: "", body: "", image_url: "", active: false, version: null, minimum_age: 0 },
  campaign: { title: "", body: "", image_url: "", cta_label: "", cta_url: "",
              active: false, version: null, minimum_age: 0 },
};

function PopupEditor({ kind, title, icon: Icon, withCta }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(POPUP_EMPTY[kind]);

  const q = useQuery({
    queryKey: ["admin-popup", kind],
    queryFn: async () => (await api.get(`/admin/popups/${kind}`)).data,
  });

  useEffect(() => {
    if (q.data) setForm({ ...POPUP_EMPTY[kind], ...q.data });
  }, [q.data, kind]);

  const saveDraft = useMutation({
    mutationFn: async () => (await api.put(`/admin/popups/${kind}`, form)).data,
    onSuccess: () => { toast.success("Entwurf gespeichert"); qc.invalidateQueries({ queryKey: ["admin-popup", kind] }); },
  });
  const publish = useMutation({
    mutationFn: async () => (await api.post(`/admin/popups/${kind}/publish`, form)).data,
    onSuccess: () => {
      // Welcome ist versioniert (einmalig pro Kunde). Kampagne ist bewusst
      // login-cycle-scoped und ignoriert die Version pro Kunde.
      toast.success(kind === "welcome"
        ? "Neue Version veröffentlicht — jeder Kunde sieht sie einmal"
        : "Werbung veröffentlicht — erscheint bei jedem neuen Login");
      qc.invalidateQueries({ queryKey: ["admin-popup", kind] });
    },
  });

  // Optimale Bildmaße pro Popup-Typ. Beide werden im Portal als großes
  // Fade-In-Modal auf dunklem Backdrop mit ``object-contain`` gerendert
  // (siehe ``PortalPopups.jsx``). 16:9 landscape füllt Desktop breit,
  // wirkt aber auch auf Mobile ohne Cropping korrekt.
  const recommendedSize = "1600 × 900 px (16:9)";
  const publishLabel = kind === "welcome"
    ? "Neue Version veröffentlichen"
    : "Werbung veröffentlichen";

  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5" data-testid={`popup-editor-${kind}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-cyan-400" />
        <div className="font-black uppercase tracking-wide">{title}</div>
        <div className="ml-auto text-xs text-zinc-500 font-mono">
          Version: {form.version ?? "–"}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-zinc-400">Titel</Label>
          <Input data-testid={`popup-${kind}-title`}
                 value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="mt-1 h-11 bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-zinc-400">Text</Label>
          <Textarea data-testid={`popup-${kind}-body`} rows={5}
                    value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
        {withCta && (
          <>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">CTA-Beschriftung</Label>
              <Input data-testid={`popup-${kind}-cta-label`}
                     value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                     className="mt-1 h-11 bg-zinc-950 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">CTA-Link</Label>
              <Input data-testid={`popup-${kind}-cta-url`}
                     value={form.cta_url || ""} onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                     placeholder="https://… oder /portal/news"
                     className="mt-1 h-11 bg-zinc-950 border-zinc-800 rounded-sm" />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-zinc-400 block mb-1">Bild</Label>
          <MediaUploadCard
            label={kind === "welcome" ? "Willkommens-Motiv" : "Werbe-Motiv"}
            value={form.image_url || ""}
            onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
            testIdPrefix={`popup-${kind}-media`}
            recommendedSize={recommendedSize}
            helperText="JPG · PNG · WebP · max. 3 MB"
            previewAspect="video"
          />
        </div>
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid={`popup-${kind}-active`}
                   checked={!!form.active}
                   onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span className="text-xs uppercase tracking-wider text-zinc-300">Aktiv</span>
          </label>
        </div>
        <div>
          <AgeSelect value={form.minimum_age}
            onChange={(v) => setForm({ ...form, minimum_age: v })}
            testId={`popup-${kind}-min-age`} />
        </div>
        <div className="sm:col-span-2">
          <ComplianceWarning testId={`popup-${kind}-compliance`} />
        </div>
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        <Button data-testid={`popup-${kind}-save-draft`} onClick={() => saveDraft.mutate()}
                className="bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-cyan-400">
          <Save size={14} className="mr-2" /> Entwurf speichern
        </Button>
        <Button data-testid={`popup-${kind}-publish`} onClick={() => publish.mutate()}
                className="bg-cyan-400 hover:bg-cyan-300">
          <Megaphone size={14} className="mr-2" /> {publishLabel}
        </Button>
      </div>
      <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
        {kind === "welcome" ? (
          <>„Veröffentlichen" erhöht die Version — jeder Kunde bekommt das Popup
          dann einmal neu angezeigt. Draft-Speichern ändert die Version nicht.</>
        ) : (
          <>Werbung wird bei <strong>jedem echten neuen Login</strong> eines Kunden
          einmal eingeblendet, solange sie aktiv ist. Reloads oder Navigation
          innerhalb derselben Session lösen sie nicht erneut aus.</>
        )}
      </div>
    </section>
  );
}


// ============ News =============================================

function NewsPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(NEWS_EMPTY);

  const list = useQuery({
    queryKey: ["admin-portal-news"],
    queryFn: async () => (await api.get("/admin/portal/news")).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, priority: +form.priority };
      if (editing) return (await api.put(`/admin/portal/news/${editing.id}`, body)).data;
      return (await api.post("/admin/portal/news", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Beitrag aktualisiert" : "Beitrag angelegt");
      setOpen(false); setEditing(null); setForm(NEWS_EMPTY);
      qc.invalidateQueries({ queryKey: ["admin-portal-news"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail ?? "Speichern fehlgeschlagen"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/portal/news/${id}`)).data,
    onSuccess: () => {
      toast.success("Beitrag gelöscht");
      qc.invalidateQueries({ queryKey: ["admin-portal-news"] });
    },
    onError: () => toast.error("Löschen fehlgeschlagen"),
  });

  const openNew = () => { setEditing(null); setForm(NEWS_EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...NEWS_EMPTY, ...p }); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="cms-news-panel">
      <div className="flex justify-end">
        <Button data-testid="cms-news-new-btn" onClick={openNew} className="bg-cyan-400 text-black hover:bg-cyan-300">
          <Plus size={16} className="mr-2" /> Neuer Beitrag
        </Button>
      </div>
      <div className="grid gap-3">
        {(list.data || []).map((p) => (
          <div key={p.id} data-testid={`cms-news-row-${p.id}`}
               className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 flex flex-wrap gap-4 items-start">
            {p.image_url ? (
              <img src={p.image_url} alt="" className="w-20 h-20 object-cover rounded-sm border border-zinc-800 shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-sm border border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-700 shrink-0">
                <ImageIcon size={20} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-black tracking-tight truncate">{p.title}</div>
                <span className={`text-[10px] uppercase font-mono tracking-widest px-2 py-0.5 rounded-sm ${p.active ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"}`}>
                  {p.active ? "aktiv" : "inaktiv"}
                </span>
                <span className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">Prio {p.priority}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{p.body}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button data-testid={`cms-news-edit-${p.id}`} size="icon" variant="ghost" onClick={() => openEdit(p)}>
                <Pencil size={16} />
              </Button>
              <Button data-testid={`cms-news-delete-${p.id}`} size="icon" variant="ghost" className="text-red-400"
                onClick={() => { if (window.confirm(`„${p.title}" löschen?`)) del.mutate(p.id); }}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
        {!list.isLoading && !list.data?.length && (
          <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500 text-sm">
            Noch keine Beiträge angelegt.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {editing ? "Beitrag bearbeiten" : "Neuer Beitrag"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Titel, Text und optional ein Bild für den Kundenportal-Feed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Titel *</Label>
              <Input data-testid="cms-news-field-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Text *</Label>
              <Textarea data-testid="cms-news-field-body" rows={6} value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400 block mb-1">Bild</Label>
              <MediaUploadCard
                label="News-Motiv"
                value={form.image_url || ""}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                testIdPrefix="cms-news-media"
                recommendedSize="1600 × 900 px (16:9)"
                helperText="JPG · PNG · WebP · max. 3 MB · Hoch- oder Querformat möglich"
                previewAspect="video"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Priorität</Label>
              <Input data-testid="cms-news-field-priority" type="number" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="cms-news-field-active" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <span className="text-xs uppercase tracking-wider text-zinc-300">Aktiv</span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <AgeSelect value={form.minimum_age}
                onChange={(v) => setForm({ ...form, minimum_age: v })}
                testId="cms-news-field-min-age" />
            </div>
            <div className="sm:col-span-2">
              <ComplianceWarning testId="cms-news-compliance" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="cms-news-save-btn" onClick={() => save.mutate()}
              disabled={!form.title || !form.body || save.isPending}
              className="bg-cyan-400 text-black hover:bg-cyan-300">
              <Save size={14} className="mr-2" />
              {save.isPending ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Public Locations =================================

function LocationsPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(LOC_EMPTY);
  const [assortmentLoc, setAssortmentLoc] = useState(null);

  const list = useQuery({
    queryKey: ["admin-portal-locations"],
    queryFn: async () => (await api.get("/admin/portal/locations")).data,
  });

  // Operations Polish 2026-08-31: freie Auswahl aus ALLEN internen
  // Lagerorten (Pflichtfeld). Keine Filterung nach Name/Typ — Admin darf
  // bewusst jeden Lagerort verknüpfen.
  const internalLocations = useQuery({
    queryKey: ["internal-locations-all"],
    queryFn: async () => (await api.get("/locations")).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      // Pflichtfeld-Vorprüfung — Backend validiert additionally.
      if (!form.internal_location_id) {
        throw new Error("Bitte einen Lagerort verknüpfen.");
      }
      const body = { ...form, priority: +form.priority,
        internal_location_id: form.internal_location_id,
        latitude: num(form.latitude),
        longitude: num(form.longitude),
        opening_hours: form.opening_hours?.trim() ? form.opening_hours : null,
        image_url: form.image_url?.trim() ? form.image_url : null,
        show_stock: !!form.show_stock,
      };
      if (editing) return (await api.put(`/admin/portal/locations/${editing.id}`, body)).data;
      return (await api.post("/admin/portal/locations", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Standort aktualisiert" : "Standort angelegt");
      setOpen(false); setEditing(null); setForm(LOC_EMPTY);
      qc.invalidateQueries({ queryKey: ["admin-portal-locations"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail ?? e?.message ?? "Speichern fehlgeschlagen"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/portal/locations/${id}`)).data,
    onSuccess: () => {
      toast.success("Standort gelöscht");
      qc.invalidateQueries({ queryKey: ["admin-portal-locations"] });
    },
    onError: () => toast.error("Löschen fehlgeschlagen"),
  });

  const openNew = () => { setEditing(null); setForm(LOC_EMPTY); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...LOC_EMPTY, ...l,
    internal_location_id: l.internal_location_id || "",
    latitude: l.latitude ?? "", longitude: l.longitude ?? "",
    opening_hours: l.opening_hours ?? "",
    image_url: l.image_url ?? "",
  }); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="cms-locations-panel">
      <div className="flex justify-end">
        <Button data-testid="cms-loc-new-btn" onClick={openNew} className="bg-cyan-400 text-black hover:bg-cyan-300">
          <Plus size={16} className="mr-2" /> Neuer Standort
        </Button>
      </div>
      <div className="grid gap-3">
        {(list.data || []).map((l) => (
          <div key={l.id} data-testid={`cms-loc-row-${l.id}`}
               className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 flex flex-wrap gap-3 items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-black tracking-tight truncate">{l.title}</div>
                <span className="text-[10px] uppercase font-mono tracking-widest px-2 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300">
                  {l.type === "automat" ? "Automat" : "SB-Shop"}
                </span>
                <span className={`text-[10px] uppercase font-mono tracking-widest px-2 py-0.5 rounded-sm ${l.active ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"}`}>
                  {l.active ? "aktiv" : "inaktiv"}
                </span>
                {l.show_stock && (
                  <span
                    data-testid={`cms-loc-show-stock-badge-${l.id}`}
                    className="text-[10px] uppercase font-mono tracking-widest px-2 py-0.5 rounded-sm bg-violet-500/20 text-violet-300"
                  >
                    Bestand sichtbar
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-1 font-mono">
                {[l.street, l.house_no].filter(Boolean).join(" ")}
                {(l.zip || l.city) ? ` · ${[l.zip, l.city].filter(Boolean).join(" ")}` : ""}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button data-testid={`cms-loc-assortment-${l.id}`} size="sm" variant="ghost"
                onClick={() => setAssortmentLoc(l)}
                className="text-cyan-400 hover:text-cyan-300 uppercase tracking-widest text-[11px]">
                Sortiment
              </Button>
              <Button data-testid={`cms-loc-edit-${l.id}`} size="icon" variant="ghost" onClick={() => openEdit(l)}>
                <Pencil size={16} />
              </Button>
              <Button data-testid={`cms-loc-delete-${l.id}`} size="icon" variant="ghost" className="text-red-400"
                onClick={() => { if (window.confirm(`„${l.title}" löschen?`)) del.mutate(l.id); }}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
        {!list.isLoading && !list.data?.length && (
          <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500 text-sm">
            Noch keine öffentlichen Standorte gepflegt.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {editing ? "Standort bearbeiten" : "Neuer Standort"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Öffentlicher Standort — sichtbar im Kundenportal-Finder.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Bezeichnung *</Label>
              <Input data-testid="cms-loc-field-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Typ *</Label>
              <select data-testid="cms-loc-field-type" value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-1 h-11 w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3">
                <option value="automat">Automat</option>
                <option value="sb_shop">SB-Shop</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Priorität</Label>
              <Input data-testid="cms-loc-field-priority" type="number" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Straße</Label>
              <Input data-testid="cms-loc-field-street" value={form.street || ""}
                onChange={(e) => setForm({ ...form, street: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Hausnr.</Label>
              <Input data-testid="cms-loc-field-houseno" value={form.house_no || ""}
                onChange={(e) => setForm({ ...form, house_no: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">PLZ</Label>
              <Input data-testid="cms-loc-field-zip" value={form.zip || ""}
                onChange={(e) => setForm({ ...form, zip: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Stadt</Label>
              <Input data-testid="cms-loc-field-city" value={form.city || ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Beschreibung</Label>
              <Textarea data-testid="cms-loc-field-desc" rows={3} value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Öffnungszeiten</Label>
              <Textarea data-testid="cms-loc-field-hours" rows={3} value={form.opening_hours || ""}
                onChange={(e) => setForm({ ...form, opening_hours: e.target.value })}
                placeholder={"z. B.\nMo–Fr 08:00–20:00\nSa 09:00–14:00\nSo geschlossen"}
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm" />
              <div className="text-[11px] text-zinc-500 mt-1">Freier Text — wird 1:1 im Standort-Finder angezeigt.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Latitude (optional)</Label>
              <Input data-testid="cms-loc-field-latitude" type="number" step="0.000001"
                value={form.latitude ?? ""}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                placeholder="50.682131"
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Longitude (optional)</Label>
              <Input data-testid="cms-loc-field-longitude" type="number" step="0.000001"
                value={form.longitude ?? ""}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                placeholder="8.301889"
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </div>
            <div className="sm:col-span-2 text-[11px] text-zinc-500 leading-relaxed">
              Mit Koordinaten öffnet der Kunden-Button „Navigiere zu" die
              Karte direkt am Zielpunkt. Ohne Koordinaten wird die Adresse
              als Freitext-Query an Google Maps übergeben.
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Lagerort *</Label>
              <select data-testid="cms-loc-field-internal-loc"
                value={form.internal_location_id || ""}
                onChange={(e) => setForm({ ...form, internal_location_id: e.target.value })}
                className="mt-1 h-11 w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3">
                <option value="">— Lagerort auswählen —</option>
                {(internalLocations.data || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} {l.code ? `· ${l.code}` : ""}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-zinc-500 mt-1">
                Pflicht — die Sortiments-Bestandsanzeige liest den echten
                Bestand aus diesem Lagerort. Admin darf jeden bestehenden
                Lagerort verknüpfen.
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="cms-loc-field-active" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <span className="text-xs uppercase tracking-wider text-zinc-300">Aktiv im Standort-Finder anzeigen</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="cms-loc-field-show-stock" checked={!!form.show_stock}
                  onChange={(e) => setForm({ ...form, show_stock: e.target.checked })} />
                <span className="text-xs uppercase tracking-wider text-zinc-300">Bestände im Club anzeigen</span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
                Automatenbild (optional)
              </Label>
              <MediaUploadCard
                testIdPrefix="cms-loc-image"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url || "" })}
                recommendedSize="1600 × 900 px (16:9)"
                helperText="JPG · PNG · WebP · max. 3 MB"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="cms-loc-save-btn" onClick={() => save.mutate()}
              disabled={!form.title || !form.internal_location_id || save.isPending}
              className="bg-cyan-400 text-black hover:bg-cyan-300">
              <Save size={14} className="mr-2" />
              {save.isPending ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AssortmentDialog loc={assortmentLoc} onClose={() => setAssortmentLoc(null)} />
    </div>
  );
}

// ============ Assortment (Sortiment) Dialog ==========================
function AssortmentDialog({ loc, onClose }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);
  const enabled = !!loc?.id;
  const list = useQuery({
    queryKey: ["cms-loc-assortment", loc?.id],
    queryFn: async () => (await api.get(`/admin/portal/locations/${loc.id}/assortment`)).data,
    enabled,
  });
  const productsQ = useQuery({
    queryKey: ["cms-product-search", debounced],
    queryFn: async () => (await api.get("/products", { params: { q: debounced, active: true } })).data,
    enabled: enabled && debounced.length >= 2,
  });
  const existingIds = new Set((list.data || []).map((r) => r.product_id));
  const add = useMutation({
    mutationFn: async (product_id) =>
      (await api.post(`/admin/portal/locations/${loc.id}/assortment`, { product_id, active: true })).data,
    onSuccess: () => { toast.success("Produkt zum Sortiment hinzugefügt"); qc.invalidateQueries({ queryKey: ["cms-loc-assortment", loc.id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Fehler beim Hinzufügen"),
  });
  const patch = useMutation({
    mutationFn: async ({ aid, active }) =>
      (await api.patch(`/admin/portal/locations/${loc.id}/assortment/${aid}`, { active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cms-loc-assortment", loc.id] }),
  });
  const remove = useMutation({
    mutationFn: async (aid) => (await api.delete(`/admin/portal/locations/${loc.id}/assortment/${aid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cms-loc-assortment", loc.id] }),
  });
  if (!enabled) return null;
  return (
    <Dialog open={enabled} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cms-assortment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide">Sortiment · {loc.title}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Sortiment = geführte Produkte am Standort. Kein Bestand wird gebucht — Kundenclub liest live aus <span className="font-mono">inventory_balances</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Produkt suchen (Name, EAN, SKU)</Label>
            <Input data-testid="cms-assortment-search" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Mind. 2 Zeichen…"
              className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm" />
            {debounced.length >= 2 && (
              <div className="mt-2 max-h-52 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-800">
                {(productsQ.data || []).slice(0, 25).map((p) => {
                  const already = existingIds.has(p.id);
                  return (
                    <div key={p.id} className="px-3 py-2 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{p.name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono truncate">{[p.ean, p.internal_id, p.brand].filter(Boolean).join(" · ")}</div>
                      </div>
                      <Button size="sm"
                        data-testid={`cms-assortment-add-${p.id}`}
                        disabled={already || add.isPending}
                        onClick={() => add.mutate(p.id)}
                        className="h-8 rounded-sm bg-cyan-500 text-zinc-950 hover:bg-cyan-400 uppercase tracking-widest text-[11px]">
                        {already ? "Im Sortiment" : "Hinzufügen"}
                      </Button>
                    </div>
                  );
                })}
                {!productsQ.isLoading && !(productsQ.data || []).length && (
                  <div className="px-3 py-3 text-xs text-zinc-500">Keine Treffer</div>
                )}
              </div>
            )}
          </div>
          <div className="border-t border-zinc-800 pt-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Aktuelles Sortiment ({(list.data || []).length})</div>
            <div className="space-y-1" data-testid="cms-assortment-list">
              {(list.data || []).map((row) => (
                <div key={row.id} className="border border-zinc-800 rounded-sm px-3 py-2 flex items-center gap-3"
                     data-testid={`cms-assortment-row-${row.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${row.active ? "" : "line-through text-zinc-500"}`}>{row.product_name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono truncate">
                      {row.product_ean || row.product_brand || "—"}
                      {row.stock != null && (<span className="ml-2 text-cyan-400">Bestand {row.stock}</span>)}
                    </div>
                  </div>
                  <label className="flex items-center gap-1 cursor-pointer text-[11px] uppercase tracking-widest text-zinc-400">
                    <input type="checkbox"
                      data-testid={`cms-assortment-active-${row.id}`}
                      checked={row.active}
                      onChange={(e) => patch.mutate({ aid: row.id, active: e.target.checked })} />
                    Aktiv
                  </label>
                  <Button size="icon" variant="ghost" className="text-rose-400"
                    data-testid={`cms-assortment-remove-${row.id}`}
                    onClick={() => { if (window.confirm(`„${row.product_name}" aus dem Sortiment entfernen?`)) remove.mutate(row.id); }}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {!list.isLoading && !(list.data || []).length && (
                <div className="text-xs text-zinc-500 italic">Noch keine Produkte im Sortiment.</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Settings (Impressum + Datenschutz + AGB + Social) =====

function LegalSection({ k, title, placeholder, value, onChange, onSave }) {
  // WICHTIG: außerhalb von SettingsPanel definieren — sonst wird die
  // Komponente bei jedem Re-Render neu gemountet und das <Textarea>
  // verliert (u. a. auf iOS) nach jedem Zeichen den Fokus.
  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5" data-testid={`cms-settings-section-${k}`}>
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="text-cyan-400" />
        <div className="font-black uppercase tracking-wide">{title}</div>
      </div>
      <Textarea data-testid={`cms-settings-${k}`} rows={10} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm" />
      <div className="flex justify-end mt-3">
        <Button data-testid={`cms-settings-save-${k}`} onClick={onSave}
          className="bg-cyan-400 hover:bg-cyan-300">
          <Save size={14} className="mr-2" /> {title} speichern
        </Button>
      </div>
    </section>
  );
}

function SettingsPanel() {
  const qc = useQueryClient();
  const [values, setValues] = useState({
    impressum: "", datenschutz: "", agb: "",
    whatsapp_url: "", instagram_url: "", facebook_url: "",
  });

  const q = useQuery({
    queryKey: ["admin-portal-settings"],
    queryFn: async () => (await api.get("/admin/portal/settings")).data,
  });

  useEffect(() => {
    if (q.data) {
      setValues((v) => ({ ...v, ...q.data }));
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: async (key) => (await api.put(`/admin/portal/settings/${key}`, { value: values[key] })).data,
    onSuccess: (data, key) => {
      // Backend hat URL evtl. normalisiert (https:// vorangestellt).
      setValues((v) => ({ ...v, [key]: data.value ?? v[key] }));
      toast.success(`${key} gespeichert`);
      qc.invalidateQueries({ queryKey: ["admin-portal-settings"] });
      qc.invalidateQueries({ queryKey: ["portal-settings"] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });

  const LegalRow = (k, title, placeholder) => (
    <LegalSection key={k} k={k} title={title} placeholder={placeholder}
                  value={values[k] || ""}
                  onChange={(v) => setValues((s) => ({ ...s, [k]: v }))}
                  onSave={() => save.mutate(k)} />
  );

  return (
    <div className="space-y-6" data-testid="cms-settings-panel">
      {LegalRow("impressum",   "Impressum",   "Angaben gemäß § 5 TMG…")}
      {LegalRow("datenschutz", "Datenschutz", "Datenschutzerklärung… (rechtsverbindliche Inhalte selbst pflegen)")}
      {LegalRow("agb",         "AGB",         "Allgemeine Geschäftsbedingungen…")}

      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle size={16} className="text-emerald-400" />
          <div className="font-black uppercase tracking-wide">Community & Social Media</div>
        </div>
        <div className="grid gap-3">
          <SocialRow icon={MessageCircle} label="WhatsApp-Community-Link" testId="cms-settings-whatsapp"
            value={values.whatsapp_url || ""}
            onChange={(v) => setValues({ ...values, whatsapp_url: v })}
            onSave={() => save.mutate("whatsapp_url")} />
          <SocialRow icon={Instagram} label="Instagram-URL" testId="cms-settings-instagram"
            value={values.instagram_url || ""}
            onChange={(v) => setValues({ ...values, instagram_url: v })}
            onSave={() => save.mutate("instagram_url")} />
          <SocialRow icon={Facebook} label="Facebook-URL" testId="cms-settings-facebook"
            value={values.facebook_url || ""}
            onChange={(v) => setValues({ ...values, facebook_url: v })}
            onSave={() => save.mutate("facebook_url")} />
        </div>
      </section>
    </div>
  );
}

function SocialRow({ icon: Icon, label, value, onChange, onSave, testId }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
      <div className="flex-1 w-full">
        <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
          <Icon size={14} /> {label}
        </Label>
        <Input data-testid={testId} value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="mt-1 h-11 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm" />
      </div>
      <Button data-testid={`${testId}-save`} onClick={onSave} className="bg-cyan-400 text-black hover:bg-cyan-300 h-11">
        <Save size={14} className="mr-2" /> Speichern
      </Button>
    </div>
  );
}
